import { ArrowBack, PlayCircleOutline, SkipNext, Stop } from '@mui/icons-material';
import { Box, CircularProgress, Typography, styled } from '@mui/material';
import { memo, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import type { PreviewControl } from '~/hooks/preview';

interface ContainerProps {
  aspectRatio?: string | number;
  children: ReactNode;
  disableMinHeight?: boolean;
}

export const Container = ({ aspectRatio = '16/9', children, disableMinHeight }: ContainerProps) => {
  return (
    <Box
      sx={{
        borderColor: 'common.black',
        borderRadius: (theme) => theme.spacing(1),
        borderStyle: 'solid',
        borderWidth: (theme) => theme.spacing(1),
        boxShadow: '0 8px 8px 0 rgba(0, 0, 0, 50%)',
        maxWidth: `calc((100vh - 5rem) * calc(${aspectRatio}))`,
        margin: '0 auto',
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          display: 'block',
          paddingBottom: `calc(100% / calc(${aspectRatio}))`,
        },
        minHeight: (theme) => (disableMinHeight ? 'inherit' : theme.spacing(5)),
        minWidth: disableMinHeight ? '100%' : 'auto',
        // Keyboard users reach the controls by Tab, so focus reveals them as hover does.
        '&:hover .control-bar, &:focus-within .control-bar': {
          opacity: 1,
        },
      }}
    >
      {children}
    </Box>
  );
};

const Iframe = styled('iframe')(({ theme }) => ({
  backgroundColor: theme.palette.common.black,
  border: 0,
  height: '100%',
  left: 0,
  position: 'absolute',
  top: 0,
  width: '100%',
}));

interface ControlBarProps {
  displayBackButton: boolean;
  onStop?: () => void;
  previewControl: PreviewControl | undefined;
}

const ControlBar = ({ displayBackButton, onStop, previewControl }: ControlBarProps) => {
  const { t } = useTranslation();

  return (
    <Box
      className="control-bar"
      display="flex"
      justifyContent="center"
      width="100%"
      height="40px"
      sx={{
        position: 'absolute',
        bottom: '8px',
        opacity: 0,
        transition: 'opacity 0.3s ease',
      }}
    >
      {(previewControl || onStop) && (
        <Box
          width="95%"
          sx={{
            backgroundColor: 'rgba(0, 0, 0, .5)',
            borderRadius: 2,
            margin: '0',
          }}
        >
          {previewControl && displayBackButton && (
            <IconButton
              label={t('common.preview.back')}
              sx={{ color: 'common.white' }}
              onClick={() => previewControl.back()}
            >
              <ArrowBack />
            </IconButton>
          )}

          {previewControl && (
            <IconButton
              label={t('common.preview.advance')}
              sx={{ color: 'common.white' }}
              onClick={() => previewControl.advance()}
            >
              <SkipNext />
            </IconButton>
          )}

          {onStop && (
            <IconButton
              label={t('common.preview.stop')}
              sx={{ color: 'common.white' }}
              onClick={onStop}
            >
              <Stop />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

const Preloader = ({ show }: { show: boolean }) => {
  const { t } = useTranslation();
  return (
    <Box
      // Faded out once loaded, but still in the DOM.
      aria-hidden={!show}
      sx={{
        alignItems: 'center',
        backgroundColor: 'common.black',
        border: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        left: 0,
        opacity: show ? 1 : 0,
        position: 'absolute',
        top: 0,
        transform: `translateY(${show ? '0%' : '-100%'})`,
        transition: 'opacity 1s ease, transform 1s ease',
        width: '100%',
      }}
    >
      <CircularProgress
        // The text below already says what is loading.
        aria-hidden
        sx={{
          color: 'common.white',
        }}
        size={60}
        thickness={2}
      />
      <Typography sx={{ color: 'common.white', mt: 2 }} variant="h4">
        {t('components.preview.loading_text')}
      </Typography>
    </Box>
  );
};

export interface PreviewProps {
  /** This can be a number or a CSS-formatted ratio string, e.g. `16/9` */
  aspectRatio?: string | number;
  /** Payload the describes what to preview. THis is dependent on the preview type. */
  payload: unknown;
  /** Set this to the proper preview URL */
  src: string;
  previewControl?: PreviewControl | undefined;
  disableMinHeight?: boolean;
  displayControlBar?: boolean;
  displayBackButton?: boolean;
  /** When provided, the control bar gets a stop button. */
  onStop?: () => void;
}

export const Preview = memo(function Preview({
  aspectRatio,
  disableMinHeight = false,
  displayControlBar = false,
  displayBackButton = false,
  onStop,
  payload,
  previewControl,
  src,
}: PreviewProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLIFrameElement>(null);

  const [showPreloader, setShowPreloader] = useState(true);

  const [iframeLoaded, setIframeLoaded] = useState(false);

  const json = payload ? JSON.stringify(payload) : null;

  useEffect(() => {
    if (iframeLoaded && json != null) ref.current?.contentWindow?.postMessage(json, '*');
  }, [iframeLoaded, json]);

  useEffect(() => {
    const onAdvance = () =>
      ref.current?.contentWindow?.postMessage(JSON.stringify({ type: 'playerAdvance' }), '*');
    previewControl?.eventTarget.addEventListener('advance', onAdvance);

    const onBack = () =>
      ref.current?.contentWindow?.postMessage(
        JSON.stringify({ type: 'input', inputType: 'button', button: 'BACK' }),
        '*',
      );
    previewControl?.eventTarget.addEventListener('back', onBack);

    return () => {
      previewControl?.eventTarget.removeEventListener('advance', onAdvance);
      previewControl?.eventTarget.removeEventListener('back', onBack);
    };
  }, [previewControl?.eventTarget]);

  useEffect(() => {
    const preloaderTimeout = setTimeout(() => {
      setShowPreloader(false);
    }, 1500);

    return () => clearTimeout(preloaderTimeout);
  }, []);

  return (
    <Container aspectRatio={aspectRatio} disableMinHeight={disableMinHeight}>
      <Iframe
        ref={ref}
        src={src}
        onLoad={() => setIframeLoaded(true)}
        id="preview"
        title={t('common.labels.preview')}
      />
      <Preloader show={showPreloader} />
      {displayControlBar && (
        <ControlBar
          displayBackButton={displayBackButton}
          onStop={onStop}
          previewControl={previewControl}
        />
      )}
    </Container>
  );
});

export interface StoppablePreviewProps extends Omit<PreviewProps, 'onStop' | 'displayControlBar'> {
  /** Start playing on mount; off shows the play button first. */
  autoplay?: boolean;
}

// Auto-playing content needs a way to stop it (WCAG 2.2.2): the control bar's stop button
// swaps the player for a play button that brings it back.
export const StoppablePreview = ({ autoplay = true, ...props }: StoppablePreviewProps) => {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(autoplay);

  if (playing) {
    return <Preview displayControlBar onStop={() => setPlaying(false)} {...props} />;
  }

  return (
    <Container aspectRatio={props.aspectRatio} disableMinHeight={props.disableMinHeight}>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: 'common.black',
          display: 'flex',
          inset: 0,
          justifyContent: 'center',
          position: 'absolute',
        }}
      >
        <IconButton
          label={t('common.preview.play')}
          onClick={() => setPlaying(true)}
          sx={{ color: 'common.white' }}
        >
          <PlayCircleOutline sx={{ fontSize: 80 }} />
        </IconButton>
      </Box>
    </Container>
  );
};
